import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { PrefsProvider, usePrefs } from "@/contexts/PrefsContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RoleShell } from "@/components/RoleShell";
import { SectionGuard, VenueRoute } from "@/components/SectionGuard";
import AdminPublicPlaces from "@/pages/admin/PublicPlaces";
import Landing from "@/pages/Landing";
import CustomerLogin from "@/pages/CustomerLogin";
import PartnerLogin from "@/pages/PartnerLogin";
import PartnerRegister from "@/pages/PartnerRegister";
import CustomerHome from "@/pages/customer/Home";
import CustomerBookings from "@/pages/customer/Bookings";
import CustomerProfile from "@/pages/customer/Profile";
import AboutPage from "@/pages/About";
import { LegalPage } from "@/components/Legal";
import VenueMyVenues from "@/pages/venue/MyVenues";
import VenueBookings from "@/pages/venue/Bookings";
import VenueTeam from "@/pages/venue/Team";
import VenueSettings from "@/pages/venue/Settings";
import VenueDetails from "@/pages/VenueDetails";
import EditVenue from "@/pages/venue/EditVenue";
import VenueCheckin from "@/pages/venue/Checkin";
import EventDetails from "@/pages/EventDetails";
import VenueEvents from "@/pages/venue/Events";
import EventForm from "@/pages/venue/EventForm";
import AdminEvents from "@/pages/admin/Events";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordPages";
import CustomerFavorites from "@/pages/customer/Favorites";
import CustomerWelcome from "@/pages/customer/Welcome";
import { InstallPrompt } from "@/components/InstallPrompt";
import AdminVenues from "@/pages/admin/Venues";
import AdminBookings from "@/pages/admin/Bookings";
import AdminCases from "@/pages/admin/Cases";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminFinance from "@/pages/admin/Finance";
import AdminAudit from "@/pages/admin/Audit";
import VenueFinance from "@/pages/venue/Finance";
import AdminAccounts from "@/pages/admin/Accounts";
import AdminSettings from "@/pages/admin/Settings";
import AdminCategories from "@/pages/admin/Categories";
import AdminActivations from "@/pages/admin/Activations";
import ChangePasswordRequired from "@/pages/customer/ChangePasswordRequired";
import CustomerSpots from "@/pages/customer/Spots";
import SpotDetails from "@/pages/SpotDetails";
import VenueReviews from "@/pages/venue/Reviews";

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<CustomerLogin />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/partner/login" element={<PartnerLogin />} />
      <Route path="/partner/register" element={<PartnerRegister />} />
      <Route path="/venues/:id" element={<VenueRoute><VenueDetails /></VenueRoute>} />
      <Route path="/events/:id" element={<SectionGuard need="events"><EventDetails /></SectionGuard>} />
      <Route path="/spots/:id" element={<SectionGuard need="spots"><SpotDetails /></SectionGuard>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/app/welcome" element={<ProtectedRoute roles={["customer"]}><CustomerWelcome /></ProtectedRoute>} />
      <Route path="/app/change-password" element={<ProtectedRoute roles={["customer"]}><ChangePasswordRequired /></ProtectedRoute>} />
      <Route element={<ProtectedRoute roles={["customer"]}><RoleShell role="customer" /></ProtectedRoute>}>
        <Route path="/app" element={<CustomerHome />} />
        <Route path="/app/bookings" element={<SectionGuard need="events"><CustomerBookings /></SectionGuard>} />
        <Route path="/app/favorites" element={<CustomerFavorites />} />
        <Route path="/app/spots" element={<SectionGuard need="spots"><CustomerSpots /></SectionGuard>} />
        <Route path="/app/profile" element={<CustomerProfile />} />
      </Route>
      <Route element={<ProtectedRoute roles={["venue"]}><RoleShell role="venue" /></ProtectedRoute>}>
        <Route path="/venue" element={<VenueMyVenues />} />
        <Route path="/venue/venues/:id/edit" element={<EditVenue />} />
        <Route path="/venue/checkin" element={<VenueCheckin />} />
        <Route path="/venue/bookings" element={<VenueBookings />} />
        <Route path="/venue/events" element={<VenueEvents />} />
        <Route path="/venue/events/new" element={<EventForm />} />
        <Route path="/venue/events/:id/edit" element={<EventForm />} />
        <Route path="/venue/reviews" element={<VenueReviews />} />
        <Route path="/venue/team" element={<VenueTeam />} />
        <Route path="/venue/finance" element={<VenueFinance />} />
        <Route path="/venue/settings" element={<VenueSettings />} />
      </Route>
      <Route element={<ProtectedRoute roles={["admin"]}><RoleShell role="admin" /></ProtectedRoute>}>
        <Route path="/admin" element={<AdminPublicPlaces />} />
        <Route path="/admin/partners" element={<AdminVenues />} />
        <Route path="/admin/events" element={<AdminEvents />} />
        <Route path="/admin/spots" element={<AdminPublicPlaces />} />
        <Route path="/admin/categories" element={<AdminCategories />} />
        <Route path="/admin/activations" element={<AdminActivations />} />
        <Route path="/admin/bookings" element={<AdminBookings />} />
        <Route path="/admin/cases" element={<AdminCases />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/finance" element={<AdminFinance />} />
        <Route path="/admin/audit" element={<AdminAudit />} />
        <Route path="/admin/accounts" element={<AdminAccounts />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const ThemedToaster = () => {
  const { theme, dir } = usePrefs();
  return <Toaster theme={theme} dir={dir} position="top-center" richColors toastOptions={{ className: "font-body rounded-2xl" }} />;
};

export default function App() {
  return (
    <PrefsProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <ThemedToaster />
        <InstallPrompt />
      </AuthProvider>
    </PrefsProvider>
  );
}
